import { useState, type ReactNode } from 'react';
import { Copy, Check, Code } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';

/**
 * HMS's own version of PulseOS's ComponentPreview (see
 * HMS_UI_REDESIGN_ANALYSIS.md) — a Preview/Code toggle card for the design
 * system reference page. Structure ported; every color/spacing value comes
 * from HMS's own tokens (bg-card, border-line, text-ink-muted, ...) so this
 * page itself demonstrates the design system rather than PulseOS's.
 */
export function ComponentPreview({
  title,
  description,
  code,
  children,
}: {
  title: string;
  description?: string;
  code: string;
  children: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState('preview');

  const copy = async () => {
    await navigator.clipboard.writeText(code.trim());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-ink-muted">{title}</h3>
        {description && <p className="text-xs text-ink-muted">{description}</p>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full overflow-hidden rounded-xl border border-line bg-card">
        <div className="flex items-center justify-between border-b border-line bg-canvas/60 px-3">
          <TabsList className="h-10 gap-3 bg-transparent p-0">
            <TabsTrigger value="preview" className="h-10 rounded-none border-b-2 border-transparent bg-transparent px-1 data-[state=active]:border-teal data-[state=active]:bg-transparent data-[state=active]:shadow-none">
              Preview
            </TabsTrigger>
            <TabsTrigger value="code" className="h-10 rounded-none border-b-2 border-transparent bg-transparent px-1 data-[state=active]:border-teal data-[state=active]:bg-transparent data-[state=active]:shadow-none">
              <Code className="mr-1.5 h-3.5 w-3.5" />
              Code
            </TabsTrigger>
          </TabsList>
          {tab === 'code' && (
            <Button variant="ghost" size="sm" onClick={copy}>
              {copied ? <Check className="mr-1.5 h-3.5 w-3.5 text-success" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          )}
        </div>

        <TabsContent value="preview" className="m-0 flex min-h-[120px] flex-wrap items-center gap-4 bg-canvas/30 p-6">
          {children}
        </TabsContent>

        <TabsContent value="code" className="m-0 border-t border-line bg-navy">
          <pre className="overflow-x-auto p-4">
            <code className="block whitespace-pre font-mono text-[12px] text-white">{code.trim()}</code>
          </pre>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export function Section({ icon, title, children }: { icon: string; title: string; children: ReactNode }) {
  return (
    <section className="space-y-5">
      <div className="flex items-center gap-2.5">
        <span className="text-xl">{icon}</span>
        <h2 className="text-lg font-bold text-ink">{title}</h2>
      </div>
      <div className="grid gap-6 md:grid-cols-2">{children}</div>
    </section>
  );
}
